/** Dev21 — Hero. Full-bleed image stage with parallax, dark overlay + banner. */

import React from 'react';
import { motion, useScroll, useTransform } from 'motion/react';
import { CalendarDays, MapPin, ChevronDown } from 'lucide-react';
import { Banner } from '@/components/ui/banner';
import { DEV21_EASE } from '../theme';
import type { Dev21Content } from '../content';

export function HeroSection({
  content,
  onRsvp,
}: {
  content: Dev21Content['hero'] & { announcement: string };
  onRsvp: () => void;
}) {
  const ref = React.useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end start'],
  });
  // Parallax: the photo drifts + scales slower than the page scroll.
  const imageY = useTransform(scrollYProgress, [0, 1], ['0%', '22%']);
  const imageScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const contentY = useTransform(scrollYProgress, [0, 1], ['0%', '40%']);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <div ref={ref} className="relative">
      {/* Dismissible announcement banner (integrated shadcn component). */}
      <Banner
        id="dev21-hero"
        variant="rainbow"
        height="3rem"
        className="text-[var(--d21-ink)]"
        rainbowColors={[
          'rgba(189,139,78,0.55)',
          'transparent',
          'rgba(236,220,193,0.7)',
          'transparent',
          'rgba(189,139,78,0.55)',
        ]}
      >
        <span className="px-6 font-medium tracking-wide">
          {content.announcement}
        </span>
      </Banner>

      <section className="relative isolate flex min-h-[92svh] items-end overflow-hidden sm:min-h-screen">
        {/* Parallax background image */}
        <motion.div
          className="absolute inset-0 -z-20"
          style={{ y: imageY, scale: imageScale }}
        >
          <img
            src={content.background}
            alt=""
            className="h-full w-full object-cover"
          />
        </motion.div>

        {/* Dark gradient overlay — guarantees text contrast on any photo */}
        <div className="absolute inset-0 -z-10 bg-[linear-gradient(180deg,rgba(20,15,10,0.55)_0%,rgba(20,15,10,0.35)_40%,rgba(20,15,10,0.82)_100%)]" />
        {/* Soft accent glow for depth */}
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(60%_40%_at_72%_18%,rgba(189,139,78,0.32),transparent_70%)]" />

        <motion.div
          style={{ y: contentY, opacity: contentOpacity }}
          className="relative mx-auto w-full max-w-6xl px-5 pb-20 pt-32 sm:px-8 sm:pb-28 lg:px-12"
        >
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: DEV21_EASE }}
            className="mb-4 text-[0.72rem] font-semibold uppercase tracking-[0.34em] text-[var(--d21-accent-soft)]"
          >
            {content.kicker}
          </motion.p>

          <motion.h1
            initial="hidden"
            animate="show"
            variants={{ hidden: {}, show: { transition: { staggerChildren: 0.12 } } }}
            className="max-w-4xl font-serif text-[clamp(2.8rem,9vw,6.8rem)] font-semibold leading-[0.95] text-white [overflow-wrap:anywhere]"
          >
            {content.title}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.35, ease: DEV21_EASE }}
            className="mt-3 font-serif text-xl italic text-white/80 sm:text-2xl"
          >
            {content.subtitle}
          </motion.p>

          {/* Detail chips + CTA */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5, ease: DEV21_EASE }}
            className="mt-9 flex flex-wrap items-center gap-3"
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm text-white/90 backdrop-blur-md">
              <CalendarDays size={16} className="text-[var(--d21-accent-soft)]" />
              {content.dateLabel}
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm text-white/90 backdrop-blur-md">
              <MapPin size={16} className="text-[var(--d21-accent-soft)]" />
              {content.venueShort}
            </span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.62, ease: DEV21_EASE }}
            className="mt-8 flex flex-wrap items-center gap-5"
          >
            <button
              type="button"
              onClick={onRsvp}
              className="rounded-full bg-[var(--d21-accent)] px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.14em] text-white shadow-lg shadow-black/30 transition hover:-translate-y-0.5 hover:bg-[var(--d21-ink)]"
            >
              RSVP Now
            </button>
            <span className="text-sm text-white/65">
              Hosted by {content.host}
            </span>
          </motion.div>
        </motion.div>

        {/* Scroll cue */}
        <motion.div
          aria-hidden
          animate={{ y: [0, 9, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute bottom-6 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-1 text-white/70"
        >
          <span className="text-[0.62rem] uppercase tracking-[0.3em]">
            Scroll
          </span>
          <ChevronDown size={18} />
        </motion.div>
      </section>
    </div>
  );
}
